import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Package, ShoppingCart, Users, DollarSign, 
  Plus, Settings, ArrowUpRight, AlertCircle 
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/admin/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching admin data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, token, navigate]);

  if (!user?.is_admin) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span className="label-text text-[#FF00FF] text-sm tracking-[0.3em]">ADMIN PANEL</span>
            <h1 className="mt-2 text-4xl heading text-[#EDEDED]">Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/products/new"
              className="btn-skew bg-[#39FF14] text-black px-6 py-3 font-bold uppercase text-sm flex items-center gap-2"
              data-testid="add-product-btn"
            >
              <Plus size={18} className="transform skewX-[12deg]" />
              <span>Add Product</span>
            </Link>
            <Link
              to="/admin/settings"
              className="btn-skew border border-[#333] text-[#EDEDED] px-6 py-3 font-bold uppercase text-sm hover:border-[#39FF14] hover:text-[#39FF14]"
              data-testid="settings-btn"
            >
              <Settings size={18} className="transform skewX-[12deg]" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-lg"></div>
            ))}
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#39FF14]/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#888] label-text text-sm">Total Products</p>
                    <p className="mt-2 text-4xl heading text-[#EDEDED]">{stats?.products || 0}</p>
                  </div>
                  <div className="p-3 bg-[#39FF14]/10 text-[#39FF14]">
                    <Package size={24} />
                  </div>
                </div>
                <Link to="/admin/products" className="mt-4 flex items-center gap-1 text-[#39FF14] text-sm hover:underline">
                  Manage Products <ArrowUpRight size={14} />
                </Link>
              </div>

              <div className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#FF00FF]/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#888] label-text text-sm">Total Orders</p>
                    <p className="mt-2 text-4xl heading text-[#EDEDED]">{stats?.orders || 0}</p>
                  </div>
                  <div className="p-3 bg-[#FF00FF]/10 text-[#FF00FF]">
                    <ShoppingCart size={24} />
                  </div>
                </div>
                <Link to="/admin/orders" className="mt-4 flex items-center gap-1 text-[#FF00FF] text-sm hover:underline">
                  View Orders <ArrowUpRight size={14} />
                </Link>
              </div>

              <div className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#00FFFF]/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#888] label-text text-sm">Total Revenue</p>
                    <p className="mt-2 text-4xl heading text-[#EDEDED]">${(stats?.revenue || 0).toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-[#00FFFF]/10 text-[#00FFFF]">
                    <DollarSign size={24} />
                  </div>
                </div>
                <p className="mt-4 text-[#888] text-sm">From confirmed orders</p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#FFA500]/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#888] label-text text-sm">Customers</p>
                    <p className="mt-2 text-4xl heading text-[#EDEDED]">{stats?.users || 0}</p>
                  </div>
                  <div className="p-3 bg-[#FFA500]/10 text-[#FFA500]">
                    <Users size={24} />
                  </div>
                </div>
                <p className="mt-4 text-[#888] text-sm">Registered users</p>
              </div>
            </div>

            {/* Pending Orders Alert */}
            {stats?.pending_orders > 0 && (
              <div className="mb-8 p-4 bg-[#FFA500]/10 border border-[#FFA500]/30 flex items-center gap-4">
                <AlertCircle className="text-[#FFA500]" size={24} />
                <div>
                  <p className="text-[#EDEDED] font-semibold">
                    {stats.pending_orders} order{stats.pending_orders !== 1 ? "s" : ""} awaiting payment
                  </p>
                  <p className="text-[#888] text-sm">Review and confirm payments when received</p>
                </div>
                <Link
                  to="/admin/orders"
                  className="ml-auto btn-skew bg-[#FFA500] text-black px-4 py-2 font-bold uppercase text-sm"
                >
                  <span>Review</span>
                </Link>
              </div>
            )}

            {/* Quick Actions & Recent Orders */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Quick Actions */}
              <div className="bg-[#0A0A0A] border border-[#222] p-6">
                <h3 className="text-xl heading text-[#EDEDED] mb-6">Quick Actions</h3>
                <div className="space-y-3">
                  <Link
                    to="/admin/products/new"
                    className="flex items-center gap-3 p-3 border border-[#222] hover:border-[#39FF14] hover:bg-[#39FF14]/5 transition-colors"
                  >
                    <Plus size={20} className="text-[#39FF14]" />
                    <span className="text-[#EDEDED]">Add New Product</span>
                  </Link>
                  <Link
                    to="/admin/products"
                    className="flex items-center gap-3 p-3 border border-[#222] hover:border-[#39FF14] hover:bg-[#39FF14]/5 transition-colors"
                  >
                    <Package size={20} className="text-[#39FF14]" />
                    <span className="text-[#EDEDED]">Manage Products</span>
                  </Link>
                  <Link
                    to="/admin/orders"
                    className="flex items-center gap-3 p-3 border border-[#222] hover:border-[#FF00FF] hover:bg-[#FF00FF]/5 transition-colors"
                  >
                    <ShoppingCart size={20} className="text-[#FF00FF]" />
                    <span className="text-[#EDEDED]">View All Orders</span>
                  </Link>
                  <Link
                    to="/admin/settings"
                    className="flex items-center gap-3 p-3 border border-[#222] hover:border-[#00FFFF] hover:bg-[#00FFFF]/5 transition-colors"
                  >
                    <Settings size={20} className="text-[#00FFFF]" />
                    <span className="text-[#EDEDED]">Store Settings</span>
                  </Link>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="lg:col-span-2 bg-[#0A0A0A] border border-[#222] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl heading text-[#EDEDED]">Recent Orders</h3>
                  <Link to="/admin/orders" className="text-[#39FF14] text-sm hover:underline">
                    View All
                  </Link>
                </div>
                {recentOrders.length === 0 ? (
                  <p className="text-[#888] text-center py-8">No orders yet</p>
                ) : (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center gap-4 p-3 border border-[#222] hover:border-[#333] transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[#EDEDED] font-mono text-sm">#{order.id.slice(0, 8)}</p>
                          <p className="text-[#888] text-sm">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[#39FF14] heading">${order.total.toFixed(2)}</p>
                          <span className={`status-badge ${order.payment_status}`}>
                            {order.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
